import Component from "@glimmer/component";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { bool, instanceOf } from "prop-types";
import RemoteHandsModel from "glesys-controlpanel/models/remote-hands-request";
import dayjs from "dayjs";
import Box from "glesys-controlpanel/components/box";
import EntityTable from "glesys-controlpanel/components/entity/table";
import Pill from "glesys-controlpanel/components/pill";
import PillInfo from "glesys-controlpanel/components/pill/info";
import PillSuccess from "glesys-controlpanel/components/pill/success";
import PillError from "glesys-controlpanel/components/pill/error";
import IconsRemoteHands from "glesys-controlpanel/components/icons/remote-hands";
import { service } from "@ember/service";
import EntityShipment from "glesys-controlpanel/components/entity/shipment";
import Label from "glesys-controlpanel/components/label";
import { t, tKey } from "ember-intl";
import { and } from "ember-truth-helpers";
import { task } from "ember-concurrency";
import modal from "glesys-controlpanel/helpers/modal";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import IconsMoreHorizontal from "glesys-controlpanel/components/icons/more-horizontal";
import { on } from "@ember/modifier";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

@forbidExtraArgs
export default class EntityRemoteHandsRequest extends Component {
  @service intl;
  @service store;

  @arg(instanceOf(RemoteHandsModel)) entity;

  @arg(bool) includeShipments = true;

  get remoteHands() {
    return this.entity;
  }

  get readableStatus() {
    const translationKeys = {
      CANCELED: tKey("remote-hands-requests.statuses.CANCELED"),
      COMPLETED: tKey("remote-hands-requests.statuses.COMPLETED"),
      IN_PROGRESS: tKey("remote-hands-requests.statuses.IN_PROGRESS"),
      PENDING: tKey("remote-hands-requests.statuses.PENDING"),
    };

    if (!translationKeys[this.remoteHands.status]) {
      return this.intl.t("remote-hands-requests.statuses.UNKNOWN");
    }

    return this.intl.t(translationKeys[this.remoteHands.status]);
  }

  get readableExecutionOption() {
    const translationKeys = {
      ASAP: tKey("remote-hands-requests.execution-options.ASAP"),
      SCHEDULED: tKey("remote-hands-requests.execution-options.SCHEDULED"),
    };

    if (!translationKeys[this.remoteHands.executionoption]) {
      return this.intl.t("remote-hands-requests.execution-options.UNKNOWN");
    }

    return this.intl.t(translationKeys[this.remoteHands.executionoption]);
  }

  get tableData() {
    return [
      {
        header: this.intl.t("label.instructions"),
        value: this.remoteHands.instructions,
      },
      {
        header: this.intl.t("label.services"),
        value: this.remoteHands.servicekeys.join(", "),
      },
      {
        header: this.intl.t("label.execution-option"),
        value: this.readableExecutionOption,
      },
      {
        header: this.intl.t("label.created"),
        value: dayjs(this.remoteHands.createdat).format("YYYY-MM-DD HH:mm"),
      },
    ];
  }

  cancelRemoteHandsTask = task(async () => {
    this.remoteHands.status = "CANCELED";

    await this.store.request(saveRecord(this.remoteHands));
  });

  <template>
    <Box
      data-test-entity-remote-hands-request
      data-test-remote-hands-request={{this.remoteHands.id}}
      @collapsable={{true}}
      ...attributes
    >
      <:header>
        <div class="flex items-center gap-4">
          <div class="p-2 border rounded-sm border-gray-200">
            <IconsRemoteHands class="size-4" />
          </div>
          <span class="text-base text-black">
            {{this.remoteHands.id}}
          </span>
          <div class="mb-1">
            {{#if this.remoteHands.isPending}}
              <Pill data-test-remote-hands-request-status-pending @text={{this.readableStatus}} />
            {{/if}}

            {{#if this.remoteHands.isInProgress}}
              <PillInfo data-test-remote-hands-request-status-in-progress @text={{this.readableStatus}} />
            {{/if}}

            {{#if this.remoteHands.isCompleted}}
              <PillSuccess data-test-remote-hands-request-status-completed @text={{this.readableStatus}} />
            {{/if}}

            {{#if this.remoteHands.isCanceled}}
              <PillError data-test-remote-hands-request-status-canceled @text={{this.readableStatus}} />
            {{/if}}
          </div>
        </div>
      </:header>
      <:headerActions>
        <div
          class="w-9 h-8 mr-2 cursor-pointer flex items-center justify-center rounded-sm border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 ml-2"
        >
          <Dropdown
            aria-label={{t "label.action-menu-aria-label" name=this.remoteHands.id}}
            data-test-select-menu
            @chevron={{false}}
          >
            <:header>
              <IconsMoreHorizontal class="w-12" />
            </:header>
            <:default>
              <DropdownDestructiveItem
                data-test-remote-hands-request-cancel
                disabled={{this.remoteHands.isNotCancelable}}
                {{on
                  "click"
                  (modal
                    "modals/confirm"
                    task=this.cancelRemoteHandsTask
                    text=(t "remote-hands-requests.cancel.text")
                    titleText=(t "remote-hands-requests.cancel.title" remoteHandsId=this.remoteHands.id)
                    buttonText=(t "remote-hands-requests.cancel.button")
                  )
                }}
              >
                {{t "button.cancel"}}
              </DropdownDestructiveItem>
            </:default>
          </Dropdown>
        </div>
      </:headerActions>
      <:default>
        <EntityTable @entity={{this.tableData}} />

        {{#if (and this.includeShipments this.remoteHands.shipments)}}
          <Label class="pt-6">{{t "label.shipments"}}</Label>
          <div class="flex flex-col gap-4">
            {{#each this.remoteHands.shipments as |shipment|}}
              <EntityShipment @entity={{shipment}} @includeRemoteHandsRequest={{false}} />
            {{/each}}
          </div>
        {{/if}}
      </:default>
    </Box>
  </template>
}
